/**
 * AB#22228 (Zendesk #9545) — roster sizes that no longer exist on the product.
 *
 * When a product's Sizes data source changes (the hockey sock move to the 2026 list, AB#22201),
 * old orders still name the old sizes. Copy Order and Copy Product must not carry those into a
 * new order: the backend answers which sizes are obsolete (a `size_check: true` dry run of copy-order / copy-product)
 * and refuses the copy (422 `obsolete_roster_sizes`) until each has a replacement chosen here.
 *
 * Request shape, shared with the API: a list, because size labels contain quotes, slashes and
 * dots that cannot be object keys in Laravel validation.
 */

export interface ObsoleteSize {
  size: string;
  rows: number;
  quantity: number;
}

export interface ObsoleteSizeReport {
  order_item_id: number | null;
  factory_product_id: string;
  product_id: number | null;
  product_name: string | null;
  sku_number: string | null;
  front_image: string | null;
  obsolete_sizes: ObsoleteSize[];
  valid_sizes: string[];
}

export interface SizeReplacement {
  order_item_id: number | null;
  factory_product_id: string;
  from: string;
  to: string;
}

/** Chosen replacements, keyed by {@link replacementKey}. Empty string = not chosen yet. */
export type SizeReplacementChoices = Record<string, string>;

export const OBSOLETE_ROSTER_SIZES_ERROR = 'obsolete_roster_sizes';

export function replacementKey(report: ObsoleteSizeReport, size: string): string {
  return `${report.order_item_id ?? ''}|${report.factory_product_id}|${size}`;
}

/** True when every obsolete size of every report has a replacement that is a current size. */
export function allSizesResolved(
  reports: ObsoleteSizeReport[],
  choices: SizeReplacementChoices
): boolean {
  return reports.every((report) =>
    report.obsolete_sizes.every((entry) => {
      const chosen = choices[replacementKey(report, entry.size)];
      return !!chosen && report.valid_sizes.includes(chosen);
    })
  );
}

/** Number of obsolete sizes still waiting for a choice. */
export function unresolvedCount(
  reports: ObsoleteSizeReport[],
  choices: SizeReplacementChoices
): number {
  return reports.reduce(
    (count, report) =>
      count +
      report.obsolete_sizes.filter((entry) => !choices[replacementKey(report, entry.size)]).length,
    0
  );
}

/** The `size_replacements` request body for the chosen replacements. */
export function buildSizeReplacements(
  reports: ObsoleteSizeReport[],
  choices: SizeReplacementChoices
): SizeReplacement[] {
  const out: SizeReplacement[] = [];
  for (const report of reports) {
    for (const entry of report.obsolete_sizes) {
      const to = choices[replacementKey(report, entry.size)];
      if (to) {
        out.push({
          order_item_id: report.order_item_id,
          factory_product_id: report.factory_product_id,
          from: entry.size,
          to,
        });
      }
    }
  }
  return out;
}

/** The reports out of a size-check response, tolerant of an empty or legacy body. */
export function reportsFromResponse(body: unknown): ObsoleteSizeReport[] {
  const products = (body as { result?: { products?: unknown } } | undefined)?.result?.products;
  return Array.isArray(products) ? (products as ObsoleteSizeReport[]) : [];
}

/**
 * The reports carried by a 422 `obsolete_roster_sizes` error, or null when the error is
 * something else — so a copy that raced a size-list change can show the picker instead of a
 * generic failure.
 */
export function reportsFromObsoleteSizeError(error: unknown): ObsoleteSizeReport[] | null {
  const response = (error as { response?: { status?: number; data?: { errors?: unknown } } })
    ?.response;
  const errors = response?.data?.errors as { code?: string; products?: unknown } | undefined;
  if (response?.status !== 422 || errors?.code !== OBSOLETE_ROSTER_SIZES_ERROR) return null;
  return Array.isArray(errors.products)
    ? (errors.products.filter(Boolean) as ObsoleteSizeReport[])
    : [];
}
